import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiService } from '@/services/api';

interface AuthUser {
  id: string;
  name?: string;
  email?: string;
  is_admin?: boolean;
  perms?: string[];
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (usernameOrEmail: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const refreshTimerRef = React.useRef<number | null>(null);

  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('auth_user');
    if (savedToken) setToken(savedToken);
    if (savedUser) setUser(JSON.parse(savedUser));
    // tenta renovar token silenciosamente caso não haja token em memória
    if (!savedToken) {
      apiService.refresh().then((data) => {
        if (data?.success && data?.data?.token) {
          const newToken = data.data.token as string;
          setToken(newToken);
          localStorage.setItem('auth_token', newToken);
          apiService.me().then((me) => {
            if (me?.success && me?.data?.user) {
              setUser(me.data.user);
              localStorage.setItem('auth_user', JSON.stringify(me.data.user));
            }
          }).catch(() => {});
          scheduleProactiveRefresh(newToken);
        }
      }).catch(() => {});
    }
  }, []);

  // agenda renovação proativa ~1 minuto antes de expirar (assumindo exp em payload JWT)
  const scheduleProactiveRefresh = useCallback((jwt: string) => {
    try {
      const [, payloadB64] = jwt.split('.');
      if (!payloadB64) return;
      const payload = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')));
      const expSec = typeof payload?.exp === 'number' ? payload.exp : null;
      if (!expSec) return;
      const msUntilExp = expSec * 1000 - Date.now();
      const refreshMs = Math.max(msUntilExp - 60_000, 5_000); // pelo menos 5s, 1min antes do exp
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      const t = window.setTimeout(async () => {
        try {
          const data = await apiService.refresh();
          if (data?.success && data?.data?.token) {
            const newToken = data.data.token as string;
            setToken(newToken);
            localStorage.setItem('auth_token', newToken);
            scheduleProactiveRefresh(newToken);
          }
        } catch {}
      }, refreshMs) as any as number;
      refreshTimerRef.current = t;
    } catch {}
  }, []);

  // quando token muda manualmente (login), reagendar
  useEffect(() => {
    if (token) scheduleProactiveRefresh(token);
  }, [token]);

  const login = useCallback(async (usernameOrEmail: string, password: string) => {
    const data = await apiService.login(usernameOrEmail, password);
    let newToken = (data?.data?.token ?? data?.token) as string | undefined;
    let newUser = (data?.data?.user ?? data?.user) as AuthUser | undefined;

    // Fallback: alguns ambientes podem não retornar o token no /login
    // Nesses casos, o refresh token HttpOnly é emitido; então buscamos o JWT via /auth/refresh
    if (!newToken) {
      try {
        const r = await apiService.refresh();
        newToken = (r?.data?.token ?? r?.token) as string | undefined;
      } catch {}
    }

    if (!newToken) {
      throw new Error((data && (data as any).error) || 'Falha no login');
    }

    setToken(newToken);
    localStorage.setItem('auth_token', newToken);
    scheduleProactiveRefresh(newToken);

    // Se não veio o usuário, buscamos pelo /auth/me
    if (!newUser) {
      try {
        const me = await apiService.me();
        newUser = (me?.data?.user ?? me?.user) as AuthUser | undefined;
      } catch {}
    }
    if (newUser) {
      setUser(newUser);
      localStorage.setItem('auth_user', JSON.stringify(newUser));
    }
  }, []);

  const logout = useCallback(() => {
    apiService.logout().finally(() => {
      setToken(null);
      setUser(null);
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    });
  }, []);

  const value = useMemo<AuthContextType>(() => ({
    user,
    token,
    isAuthenticated: !!token,
    login,
    logout
  }), [user, token, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}


